import { useState, useCallback, useEffect, useRef } from "react";
import NudgeService from "../services/NudgeService";
import { useCampaign } from "../context/campaign";

// "Disable AI" is scoped to the web session (browser tab), not the mounted
// component: it must survive navigating between coaching phases (e.g. Plan ->
// Do), which are separate routes/components, while still resetting once the
// browser tab/session ends. sessionStorage is the right primitive for that.
// Namespaced by sessionId so multiple coaching sessions opened in the same
// tab don't leak the toggle into each other. See COACH-2122.
const getAiDisabledStorageKey = sessionId =>
  sessionId ? `ai_nudge_disabled:${sessionId}` : null;

const readAiDisabled = sessionId => {
  const key = getAiDisabledStorageKey(sessionId);
  if (!key) return false;
  try {
    return window.sessionStorage.getItem(key) === "true";
  } catch (e) {
    // sessionStorage unavailable (private browsing / disabled) - degrade to
    // in-memory-only behavior rather than throwing.
    return false;
  }
};

// The native `storage` event only fires in *other* tabs/windows, never in the
// document that made the write - so it can't sync two useNudgePopover
// instances mounted at once in the same form (e.g. SessionsConversationalForm
// calls the hook once per subject). Broadcast a same-window custom event too
// so every mounted instance for a given sessionId stays in sync immediately.
const AI_DISABLED_EVENT = "coach:ai-nudge-disabled-changed";

const writeAiDisabled = (sessionId, disabled) => {
  const key = getAiDisabledStorageKey(sessionId);
  if (!key) return;
  try {
    if (disabled) {
      window.sessionStorage.setItem(key, "true");
    } else {
      window.sessionStorage.removeItem(key);
    }
  } catch (e) {
    // sessionStorage unavailable - nothing to persist, in-memory state still works.
  }
  window.dispatchEvent(
    new CustomEvent(AI_DISABLED_EVENT, { detail: { sessionId, disabled } })
  );
};

const useNudgePopover = (
  sessionId,
  defaultSubjectIdOrFn,
  onComplete,
  getSessionAnswers
) => {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState(null);
  const [streamedText, setStreamedText] = useState("");
  const [nudgeType, setNudgeType] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [lastRequest, setLastRequest] = useState(null);
  // The coach's own manual "disable AI" preference (sessionStorage-backed).
  const [rawAiDisabled, setAiDisabledState] = useState(() =>
    readAiDisabled(sessionId)
  );
  // AI Coaching pilot region gate: block AI regardless of the coach's own
  // toggle when their region isn't in the pilot allowlist. Folded in here
  // (rather than at each call site) so every consumer of this hook - both
  // prop-driven ones and ones that invoke handleNudgeRequest directly - is
  // covered uniformly. See alt-ai-coaching-pilot.md.
  const { isAiCoachingEnabled } = useCampaign();
  const aiDisabled = rawAiDisabled || !isAiCoachingEnabled;
  const currentRequestRef = useRef(null);

  // sessionId can arrive asynchronously (e.g. resolved after an initial
  // fetch) in some forms - re-sync from storage whenever it changes/becomes
  // available so the toggle reflects the correct session, not a flash of the
  // default "enabled" state.
  useEffect(() => {
    setAiDisabledState(readAiDisabled(sessionId));
  }, [sessionId]);

  // Keep every useNudgePopover instance for the same sessionId in sync
  // immediately (e.g. SessionsConversationalForm mounts this hook once per
  // subject) - see the AI_DISABLED_EVENT comment above.
  useEffect(() => {
    if (!sessionId) return undefined;
    const onAiDisabledChanged = e => {
      if (e.detail?.sessionId === sessionId) {
        setAiDisabledState(e.detail.disabled);
      }
    };
    window.addEventListener(AI_DISABLED_EVENT, onAiDisabledChanged);
    return () =>
      window.removeEventListener(AI_DISABLED_EVENT, onAiDisabledChanged);
  }, [sessionId]);

  const setAiDisabled = useCallback(
    disabled => {
      setAiDisabledState(disabled);
      writeAiDisabled(sessionId, disabled);
    },
    [sessionId]
  );

  const isOpen = Boolean(popoverAnchorEl);

  const getSubjectId = useCallback(() => {
    if (typeof defaultSubjectIdOrFn === "function") {
      return defaultSubjectIdOrFn();
    }
    return defaultSubjectIdOrFn;
  }, [defaultSubjectIdOrFn]);

  const handleNudgeRequest = useCallback(
    (
      questionId,
      questionText,
      answer,
      anchorEl,
      subjects,
      setNudgeTypeState
    ) => {
      if (aiDisabled) return;
      if (!answer?.trim()) return;

      // Open popover first
      setPopoverAnchorEl(anchorEl);

      // Validate required fields
      if (!sessionId || !subjects?.length) {
        setError("sessionId and subjects are required.");
        setStreaming(false);
        return;
      }

      // Cancel previous request if exists
      if (currentRequestRef.current) {
        currentRequestRef.current = null;
      }

      setStreamedText("");
      setNudgeType("");
      setNudgeTypeState?.("");
      setError(null);
      setStreaming(true);

      const sessionAnswers =
        typeof getSessionAnswers === "function"
          ? getSessionAnswers()
          : undefined;

      const requestData = {
        sessionId,
        questionId,
        questionText,
        answer,
        subjects,
        sessionAnswers,
      };

      setLastRequest({ ...requestData, anchorEl });
      currentRequestRef.current = requestData;

      NudgeService.evaluateAnswerStream(
        requestData,
        token => {
          if (currentRequestRef.current === requestData) {
            setStreamedText(prev => prev + token);
          }
        },
        streamResult => {
          if (currentRequestRef.current === requestData) {
            setStreaming(false);
            setStreamedText(streamResult.nudge || "");
            setNudgeType(streamResult.evaluationType || "");
            setNudgeTypeState?.(streamResult.evaluationType || "");
            if (onComplete) onComplete(streamResult);
          }
        },
        err => {
          if (currentRequestRef.current === requestData) {
            setStreaming(false);
            // Check if error message contains status code 400 or above
            const statusMatch = err.message?.match(/status:\s*(\d+)/);
            if (statusMatch) {
              const statusCode = parseInt(statusMatch[1], 10);
              if (statusCode >= 400) {
                setError("Oops, something went wrong.");
                return;
              }
            }
            setError(err.message || "Failed to get AI feedback");
          }
        }
      );
    },
    [sessionId, onComplete, getSubjectId, aiDisabled, getSessionAnswers]
  );

  const handleDisableAI = useCallback(() => {
    setAiDisabled(true);
    currentRequestRef.current = null;
    setPopoverAnchorEl(null);
    setStreamedText("");
    setNudgeType("");
    setStreaming(false);
    setError(null);
  }, [setAiDisabled]);

  const handleEnableAI = useCallback(() => {
    setAiDisabled(false);
  }, [setAiDisabled]);

  const handleClosePopover = useCallback(() => {
    currentRequestRef.current = null;
    setPopoverAnchorEl(null);
    setStreamedText("");
    setNudgeType("");
    setStreaming(false);
    setError(null);
  }, []);

  const clearNudgeState = useCallback(() => {
    setStreamedText("");
    setNudgeType("");
    setStreaming(false);
    setError(null);
  }, []);

  const handleFeedback = useCallback(type => {
    console.log("Feedback:", type);
  }, []);

  const handleRefresh = useCallback(() => {
    if (lastRequest) {
      handleNudgeRequest(
        lastRequest.questionId,
        lastRequest.questionText,
        lastRequest.answer,
        lastRequest.anchorEl,
        lastRequest.subjects
      );
    }
  }, [lastRequest, handleNudgeRequest]);

  return {
    popoverAnchorEl,
    streamedText,
    nudgeType,
    streaming,
    error,
    isOpen,
    aiDisabled,
    // The coach's own manual toggle, unmixed with the region gate - lets
    // callers show "you turned AI off" UI (e.g. AiBanner) without it also
    // firing when AI is simply unavailable for the region.
    manuallyDisabled: rawAiDisabled,
    handleNudgeRequest,
    handleClosePopover,
    handleFeedback,
    handleRefresh,
    handleDisableAI,
    handleEnableAI,
    clearNudgeState,
  };
};

export default useNudgePopover;
